import { defineComponent as _defineComponent } from 'vue'
import 'mapbox-gl/dist/mapbox-gl.css'
import mapboxgl from 'mapbox-gl'
import { ref, provide, shallowRef, onMounted, watch, toRef } from 'vue'
import { useState } from 'vuex-composition-helpers'
import { usePersistedFieldViewPosition } from '../usePersistedFieldViewPosition'
import JobClusterPin from './icons/job-cluster-pin'
import CirclePin from './icons/circle-pin'
import OwnedLocationPin from './icons/owned-location-pin'
import PartnerLocationPin from './icons/partner-location-pin'
import { MapContextKey } from './context'
import { getInstance } from '@/auth'
import { RequestInterceptor } from './RequestInterceptor'
import { useJobFilterStore } from '../JobsFilterStore'
import { isEqual } from 'lodash'
import { useGetMapLayersQuery } from '@/api/mapping'
import { useNavStore } from '@/components/nav/navStore'


export default /*#__PURE__*/_defineComponent({
  __name: 'FieldMap',
  props: {
    center: null,
    zoom: null,
    hash: { type: Boolean },
    mapStyle: { default: 'mapbox://styles/mapbox/satellite-streets-v12?optimize=true' },
    rememberPosition: { type: Boolean },
    mini: { type: Boolean }
  },
  emits: ["clickFeature", "clickCluster", "sourceLoaded", "move"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
  (
    event: 'clickFeature',
    features: mapboxgl.MapboxGeoJSONFeature[],
    eventData: mapboxgl.MapMouseEvent & mapboxgl.EventData
  )
  (event: 'clickCluster', data: number[])
  (event: 'sourceLoaded', sourceId: string)
  (event: 'move', data: mapboxgl.LngLatLike)
}), expose: any, slots: any, attrs: any }) {

const props = __props as { center?: mapboxgl.LngLatLike, zoom?: number, hash?: boolean, mapStyle: string, rememberPosition?: boolean, mini?: boolean }





const map = shallowRef<mapboxgl.Map>()
const popup = shallowRef<mapboxgl.Popup>()

const container = ref<HTMLElement>()

const ready = ref<boolean>()
const imageCache = new Map()

const { previousPosition, saveMapPosition } = usePersistedFieldViewPosition(map)

const { mapboxAccessToken, mapCenterPoint } = useState('organizationConfig', [
  'mapboxAccessToken',
  'mapCenterPoint',
])

const userAuthToken = ref<string>()

const { data: mapLayersConfigData } = useGetMapLayersQuery()

const authenticationInterceptor: RequestInterceptor = (
  url,
  resourceType,
  currentRequest
) => {
  if (resourceType === 'Source' && url.startsWith(window.location.origin)) {
    return {
      ...currentRequest,
      headers: {
        ...currentRequest.headers,
        Authorization: `Bearer ${userAuthToken.value}`,
      },
    }
  }

  return currentRequest
}

const requestHandlers = ref<{ [key: string]: RequestInterceptor }>({
  auth: authenticationInterceptor,
})

const addRequestHandler = (key: string, handler: RequestInterceptor) => {
  requestHandlers.value[key] = handler
}

const removeRequestHandler = (key: string) => {
  delete requestHandlers.value[key]
}

const initializeMap = async () => {
  if (map.value || !mapboxAccessToken.value) return

  const authService = getInstance()
  userAuthToken.value = await authService.getTokenSilently()

  const defaultPosition = {
    center: props.center ??
      previousPosition.value?.center ?? {
        lat: mapCenterPoint.value.latitude,
        lng: mapCenterPoint.value.longitude,
      },
    zoom:
      props.zoom ?? previousPosition.value?.zoom ?? mapCenterPoint.value.zoom,
  }

  mapboxgl.accessToken = mapboxAccessToken.value
  map.value = new mapboxgl.Map({
    container: container.value as HTMLElement,
    style: 'mapbox://styles/mapbox/satellite-streets-v11?optimize=true',
    center: defaultPosition.center,
    zoom: defaultPosition.zoom,
    hash: props.hash,
    transformRequest: onTransformOutgoingRequest,
  })
    .on('load', async (e) => {
      configureIcons(e.target)

      e.target.addControl(new mapboxgl.NavigationControl(), 'bottom-right')

      ready.value = true
    })
    .on('sourcedata', async (e) => {
      if (
        e &&
        'isSourceLoaded' in e &&
        e.isSourceLoaded &&
        'sourceId' in e &&
        ['locations', 'resources'].includes(e.sourceId)
      ) {
        emits('sourceLoaded', e.sourceId as string)
      }
    })
    .on('move', (e) => {
      emits('move', e.target.getCenter())
    })
    .on('moveend', () => {
      if (props.rememberPosition) saveMapPosition()
    })

  popup.value = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
  })
}

const configureIcons = (map: mapboxgl.Map) => {
  map.on('styleimagemissing', async ({ id }) => {
    if (imageCache.has(id) || map.hasImage(id)) {
      return
    }

    if (id.startsWith('job-cluster')) {
      map.addImage(id, JobClusterPin(id), { pixelRatio: 4 })
    }

    if (id.startsWith('circle')) {
      map.addImage(id, CirclePin(id), { pixelRatio: 4 })
    }

    if (id.startsWith('owned')) {
      map.addImage(id, OwnedLocationPin(id), { pixelRatio: 4 })
    }

    if (id.startsWith('partner')) {
      map.addImage(id, PartnerLocationPin(id), { pixelRatio: 4 })
    }

    if (id.startsWith('arrow')) {
      imageCache.set(
        id,
        new Promise<void>((resolve, reject) => {
          import(`@/components/common/unit-icons/${id}.png`).then((path) => {
            map.loadImage(path.default, (error, image) => {
              if (error) reject(error)
              if (image) map.addImage(id, image, { pixelRatio: 4 })
              resolve()
            })
          })
        })
      )
    }
  })
}

const onTransformOutgoingRequest: mapboxgl.TransformRequestFunction = (
  url: string,
  resourceType: mapboxgl.ResourceType
) => {
  return Object.values(requestHandlers.value).reduce(
    (result, nextHandler) => {
      return {
        ...result,
        ...nextHandler(url, resourceType, result),
      }
    },
    { url }
  )
}

const isMini = toRef(props, 'mini')

const store = useJobFilterStore()
onMounted(async () => {
  initializeMap()
})

watch(() => mapboxAccessToken, initializeMap)
// When the map is mounted, it is rendered fixed to a certain dimension/size
// Therefore, whenever we are showing or hiding the job filter side panel, we need to resize the map
// Otherwise, showing/hiding the side panel may make the map not fit into the entire screen
watch(
  () => store.isShowingFilters,
  () => {
    map.value?.resize()
  }
)

const navStore = useNavStore()
watch(
  () => navStore.isNavVisible,
  () => {
    map.value?.resize()
  }
)

watch(
  () => props.center,
  (newCenter, oldCenter) => {
    if (map.value && newCenter && !isEqual(newCenter, oldCenter)) {
      map.value.jumpTo({ center: newCenter })
    }
  }
)

provide(MapContextKey, {
  map,
  popup,
  mini: isMini,
  addRequestHandler,
  removeRequestHandler,
})

expose({ map })

return { __sfc: true,props, emits, map, popup, container, ready, imageCache, previousPosition, saveMapPosition, mapboxAccessToken, mapCenterPoint, userAuthToken, mapLayersConfigData, authenticationInterceptor, requestHandlers, addRequestHandler, removeRequestHandler, initializeMap, configureIcons, onTransformOutgoingRequest, isMini, store, navStore }
}

})